import React from 'react';
import { BottomNavigation, BottomNavigationAction, Paper, Avatar } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonIcon from '@mui/icons-material/Person';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useMode } from '../context/ModeContext';
import { useAuth } from '../context/AuthContext';

const StyledBottomNav = styled(Paper)(({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    display: 'none', // Hidden by default
    [theme.breakpoints.down('sm')]: {
        display: 'block', // Only show on mobile
    },
}));

// Styled BottomNavigationAction to make highlighted state more subtle
const StyledBottomNavigationAction = styled(BottomNavigationAction)(({ theme }) => ({
    padding: '6px 0',
    minWidth: 0,
    maxWidth: '100%',
    '&.Mui-selected': {
        // Make the highlight more subtle
        color: '#34A853',
        // Prevent growing too large when selected
        padding: '6px 0',
        fontSize: '0.85rem',
    },
    '& .MuiBottomNavigationAction-label': {
        fontSize: '0.75rem',
        '&.Mui-selected': {
            fontSize: '0.85rem',
        },
    },
}));

// Styled Avatar for profile icon
const ProfileAvatar = styled(Avatar)(({ theme }) => ({
    width: 24,
    height: 24,
    fontSize: '0.75rem',
    border: '1px solid #E8F5E9',
}));

const MobileBottomNavigation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { mode } = useMode(); // mode is either "Borrowing" or "Lending"
    const { currentUser: user } = useAuth();

    // Helper function to get user initials (same as in Header)
    const getUserInitials = (name) => {
        if (!name) return '';
        const nameParts = name.split(' ');
        return `${nameParts[0]?.[0]?.toUpperCase() || ''}${nameParts[1]?.[0]?.toUpperCase() || ''}`.trim();
    };

    // Determine the current tab value based on the location and mode
    const getCurrentValue = () => {
        const path = location.pathname;

        if (mode === 'Borrowing') {
            if (path === '/' || path.startsWith('/listings')) return 'browser';
            if (path.startsWith('/wishlist')) return 'favorites';
            if (path.startsWith('/bookings')) return 'transactions';
            if (path.startsWith('/conversations')) return 'messages';
            if (path.startsWith('/profile-menu') || path.startsWith('/profile')) return 'profile';
            return 'browser';
        } else { // Lending mode
            if (path.startsWith('/my-listings') || path.startsWith('/create-listing')) return 'listings';
            if (path.startsWith('/lending-bookings')) return 'transactions';
            if (path.startsWith('/conversations')) return 'messages';
            if (path.startsWith('/profile-menu') || path.startsWith('/profile')) return 'profile';
            return 'listings';
        }
    };

    // Navigate to appropriate route when tab is changed
    const handleNavigate = (newValue) => {
        if (mode === 'Borrowing') {
            switch (newValue) {
                case 'browser':
                    navigate('/');
                    break;
                case 'favorites':
                    navigate('/wishlist');
                    break;
                case 'transactions':
                    navigate('/bookings');
                    break;
                case 'messages':
                    navigate('/conversations');
                    break;
                case 'profile':
                    navigate('/profile-menu'); // Use the mobile profile menu route
                    break;
                default:
                    navigate('/');
            }
        } else { // Lending mode
            switch (newValue) {
                case 'listings':
                    navigate('/my-listings');
                    break;
                case 'transactions':
                    navigate('/lending-bookings');
                    break;
                case 'messages':
                    navigate('/conversations');
                    break;
                case 'profile':
                    navigate('/profile-menu');
                    break;
                default:
                    navigate('/my-listings');
            }
        }
    };

    // Create profile icon (either Avatar with photo or initials, or PersonIcon as fallback)
    const getProfileIcon = () => {
        if (user) {
            if (user.profile_picture) {
                return <ProfileAvatar src={user.profile_picture} alt={user.name} />;
            } else {
                return <ProfileAvatar>{getUserInitials(user.name)}</ProfileAvatar>;
            }
        }
        return <PersonIcon />;
    };

    // Define navigation items per user mode.
    const navItems = mode === 'Borrowing'
        ? [
            { label: 'Browser', value: 'browser', icon: <SearchIcon /> },
            { label: 'Favorites', value: 'favorites', icon: <BookmarkBorderIcon /> },
            { label: 'Transactions', value: 'transactions', icon: <SwapHorizIcon /> },
            { label: 'Messages', value: 'messages', icon: <ChatBubbleOutlineIcon /> },
            { label: 'Profile', value: 'profile', icon: getProfileIcon() },
        ]
        : [
            { label: 'Listings', value: 'listings', icon: <ViewListIcon /> },
            { label: 'Transactions', value: 'transactions', icon: <SwapHorizIcon /> },
            { label: 'Messages', value: 'messages', icon: <ChatBubbleOutlineIcon /> },
            { label: 'Profile', value: 'profile', icon: getProfileIcon() },
        ];

    return (
        <StyledBottomNav elevation={3}>
            <BottomNavigation
                value={getCurrentValue()}
                onChange={(event, newValue) => handleNavigate(newValue)}
                showLabels
            >
                {navItems.map((item) => (
                    <StyledBottomNavigationAction
                        key={item.value}
                        label={item.label}
                        value={item.value}
                        icon={item.icon}
                    />
                ))}
            </BottomNavigation>
        </StyledBottomNav>
    );
};

export default MobileBottomNavigation;